import { ColumnDef } from "@tanstack/react-table";
import { PaymentTransaction } from "@/types/payment";
import moment from "moment";
import { formatCurrency } from "@/utils/helper/currency";

export const createPaymentTableColumns = (onTransactionClick?: (transaction: PaymentTransaction) => void): ColumnDef<PaymentTransaction>[] => [
  {
    accessorKey: "transaction_id",
    header: "Transaction ID",
    cell: ({ row }) => {
      const transactionId = row.getValue("transaction_id") as string;
      const paidBy = row.getValue("paid_by") as string | null;
      const shouldShowDash = !paidBy; // Show "-" if paid_by is null
      
      return (
        <span 
          className={`text-text-primary font-normal leading-normal text-13 font-mono ${onTransactionClick && !shouldShowDash ? 'cursor-pointer hover:text-text-primary hover:underline' : ''}`}
          onClick={() => !shouldShowDash && onTransactionClick?.(row.original)}
        >
          {shouldShowDash ? "-" : transactionId}
        </span>
      );
    },
  },
  {
    accessorKey: "proof_id",
    header: "Proof ID",
    cell: ({ row }) => (
      <span className="text-text-default font-normal leading-normal text-13 font-mono">
        {row.getValue("proof_id")}
      </span>
    ),
  },
  {
    accessorKey: "emp_id",
    header: "Employee ID",
    cell: ({ row }) => (
      <span className="text-text-default font-normal leading-normal text-13">
        {row.getValue("emp_id")}
      </span>
    ),
  },
  {
    accessorKey: "employee_name",
    header: "Employee name",
    cell: ({ row }) => {
      const transaction = row.original;
      const empName = transaction.empName;
      const profileLabel = empName?.label || transaction.employee_name;
      
      if (!empName?.profileText || !profileLabel || profileLabel === "-") {
        return <span className="font-normal !leading-normal text-13 text-red-500">-</span>;
      }
      
      return (
        <div className="flex items-center gap-1">
          {empName?.profilePic ? (
            <img
              src={empName.profilePic}
              alt={profileLabel}
              className="h-5 w-5 rounded-full object-cover object-center"
              style={{ minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }}
            />
          ) : (
            <div className="flex w-5 h-5 !min-h-5 !min-w-5 items-center justify-center border border-text-primary rounded-full text-text-primary font-bold text-[8px]">
              {empName.profileText}
            </div>
          )}
          <span className="font-normal !leading-normal text-13">
            {profileLabel}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "paid_by",
    header: "Paid by",
    cell: ({ row }) => {
      const paidBy = row.getValue("paid_by") as string | null;
      const status = row.getValue("status") as string;
      
      // Show "-" if status is not "Paid" or if paidBy is null/empty
      if (status !== "Paid" || !paidBy) {
        return (
          <span className="text-text-default font-normal leading-normal text-13">
            -
          </span>
        );
      }
      
      return (
        <span className="text-text-default font-normal leading-normal text-13">
          {paidBy}
        </span>
      );
    },
  },
  {
    accessorKey: "document_price",
    header: "Document price",
    cell: ({ row }) => {
      const transaction = row.original;
      const currency = transaction.currency || "INR"; // Default to INR if not provided
      return (
        <span className="text-text-default font-normal leading-normal text-13">
          {formatCurrency(transaction.document_price, currency)}
        </span>
      );
    },
  },
  {
    accessorKey: "received_on",
    header: "Received on",
    cell: ({ row }) => {
      const date = row.getValue("received_on") as string | null;
      return (
        <span className="text-text-default font-normal leading-normal text-13">
          {date ? moment(date).format("DD MMM, YYYY") : "-"}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      const statusConfig = {
        Paid: { 
          bg: "#f2f9f6", 
          text: "#2c996d", 
          label: "Received" 
        },
        Created: { 
          bg: "#fffaf3", 
          text: "#ffa82d", 
          label: "Waiting" 
        },
        Expired: { 
          bg: "#fef6f6", 
          text: "#ef7778", 
          label: "Expired" 
        },
      };

      const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.Created;

      return (
        <span 
          className="inline-flex items-center px-3 py-1 rounded-[6px] text-xs font-medium"
          style={{ 
            backgroundColor: config.bg, 
            color: config.text 
          }}
        >
          {config.label}
        </span>
      );
    },
  },
];

// Export the original columns for backward compatibility
export const paymentTableColumns = createPaymentTableColumns();
